import { useQuestionBuilder } from 'lib-node-question-builder';
import { formIsNot, or } from 'lib-node-question-builder/utils';
import { useRef, useState } from 'react';

import type {
  DtoV1Borrower,
  DtoV1EnumOccupancyType,
  DtoV1EnumPropertyStatus,
  DtoV1EnumPropertyType,
  DtoV1RealEstateOwnedSaveRealEstateOwnedSummaryRequest,
} from '~/__generated__/api';
import { US_STATES_LABEL_VALUE_ARRAY } from '~/common/constants';
import { COUNTRY_LABEL_VALUE_ARRAY } from '~/common/constants/countries';
import useUrl from '~/common/hooks/useUrl';
import Loan1003FormContainer from '~/pages/Loan1003DataGrids/common/components/Loan1003FormContainer';
import Loan1003FormSection from '~/pages/Loan1003DataGrids/common/components/Loan1003FormSection';

import { READONLY_CONDITION, formatName } from '../../common/utils/utils';
import { useSaveRealEstateOwnedSummaryMutation } from '../hooks';
import { useRealEstateOwnedStore } from '../useRealEstateOwnedStore';
import {
  ESCROWED_ON_PROPERTY_OPTIONS,
  NON_SUBJECT_PROP_NOT_TWO_FOUR_UNIT_OCCUPANCY,
  NON_SUBJECT_PROP_TWO_FOUR_UNIT_OCCUPANCY,
  REO_GROSS_RENTAL_INCOME_PROPERTY_TYPE,
  REO_PROPERTY_STATUS,
  REO_PROPERTY_TYPE,
  SUBJECT_PROPERTY_PROPERTY_STATUS_OPTIONS,
  SUBJECT_PROP_NOT_TWO_FOUR_UNIT_OCCUPANCY,
  SUBJECT_PROP_TWO_FOUR_UNIT_OCCUPANCY,
} from './constants';

const getDefaultValues = (rowToEdit: RowToEdit) => {
  return {
    customerIdentifiers: rowToEdit.associatedBorrowers.map(
      (identifiers: DtoV1Borrower) => String(identifiers.customerRecordId),
    ),
    addressLine1: rowToEdit.addressLine1,
    unit: rowToEdit.unit,
    city: rowToEdit.city,
    state: rowToEdit.state,
    country: rowToEdit.country,
    zipCode: rowToEdit.zipCode,
    propertyStatus: rowToEdit.propertyStatus,
    propertyType: rowToEdit.propertyTypeId,
    subjectProperty: rowToEdit.isSubjectProperty,
    marketValue: rowToEdit.marketValue ?? 0,
    occupancyType: rowToEdit.occupancyType ?? '',
    mortgagePayment: rowToEdit.mortgagePayment ?? +rowToEdit.mortgagePayment,
    netRent: rowToEdit.netRentalIncome ?? +rowToEdit.netRentalIncome,
    associatedLiabilities: rowToEdit.associatedLiabilities.map(
      (liabilities: AssociatedLiability) =>
        liabilities.creditorName + ' ' + liabilities.accountNumber,
    ),
    mortgagePaymentIncludesTaxesAndInsurance: true,
    mortgageBalance: rowToEdit.mortgageBalance ?? 0,
    escrowedOnProperty: rowToEdit.escrowedOnProperty,
    monthlyPropertyTaxes: rowToEdit.monthlyTaxes ?? 0,
    monthlyHazardInsurance: rowToEdit.monthlyHazardInsurance ?? 0,
    monthlyFloodInsurance: rowToEdit.monthlyFloodInsurance ?? 0,
    monthlyAssociationDues: rowToEdit.monthlyAssociationDues ?? 0,
    grossRentalIncome: rowToEdit.grossRentalIncome ?? 0,
    hasGrossRentalIncomeChanged: false,
    leaseGroundRents: rowToEdit.leaseGroundRents ?? 0,
    totalMonthlyExpenses: rowToEdit.totalMonthlyExpenses ?? 0,
    netRentalIncome: rowToEdit.netRentalIncome ?? 0,
  };
};

type RealEstateOwnedSummariesFormProps = {
  rowToEdit: RowToEdit;
  allBorrowerOptions?: DtoV1Borrower[];
  isWriteAccessDisabled?: boolean;
  dropdownOptions?: Record<string, { label: string; value: any }[]>;
};

type RealEstateOwnedSummaryFormData = {
  customerIdentifiers: string[];
  subjectProperty: boolean;
  mortgagePayment: number;
  netRentalIncome: number;
  addressLine1: string;
  unit: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  propertyType: number | string;
  propertyStatus: number | string;
  occupancyType: number | string;
  marketValue: number;
  mortgagePaymentIncludesTaxesAndInsurance: boolean;
  mortgageBalance: number;
  escrowedOnProperty: number;
  monthlyPropertyTaxes: number | null;
  monthlyHazardInsurance: number | null;
  monthlyFloodInsurance: number;
  monthlyAssociationDues: number;
  grossRentalIncome: number;
  hasGrossRentalIncomeChanged: boolean;
  leaseGroundRents: number;
  totalMonthlyExpenses: number;
};

type AssociatedLiability = {
  creditorName?: string;
  accountNumber?: string;
};

type FormOption = { label: string; value: number };

export type RowToEdit = {
  addressId: number | { addressId: number; addressLdId: number };
  addressLine1: string;
  unit: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  propertyStatus: number | string;
  propertyTypeId: number | string;
  isSubjectProperty: boolean;
  marketValue: number;
  occupancyType: number | string;
  mortgagePayment: number;
  netRentalIncome: number;
  associatedLiabilities: AssociatedLiability[];
  associatedBorrowers: DtoV1Borrower[];
  mortgageBalance: number;
  escrowedOnProperty: number;
  monthlyTaxes?: number;
  monthlyHazardInsurance: number;
  monthlyFloodInsurance: number;
  monthlyAssociationDues: number;
  grossRentalIncome: number | string;
  leaseGroundRents: number;
  totalMonthlyExpenses: number;
};

export default function RealEstateOwnedSummariesForm({
  rowToEdit,
  allBorrowerOptions,
  isWriteAccessDisabled,
  dropdownOptions,
}: Readonly<RealEstateOwnedSummariesFormProps>) {
  const { obfuscatedLoanIdentifier } = useUrl();
  const closeForm = useRealEstateOwnedStore((state) => state.closeForm);
  const { mutate, isLoading } = useSaveRealEstateOwnedSummaryMutation();

  const isSubjectProperty = rowToEdit.isSubjectProperty;
  const isAssociatedLiabilitiesVisible =
    rowToEdit.associatedLiabilities.length > 0;
  const creditorNames = rowToEdit.associatedLiabilities.reduce(
    (acc: string, { creditorName, accountNumber }) => {
      if (creditorName || accountNumber) {
        acc +=
          (acc ? ', ' : '') +
          [creditorName, accountNumber].filter(Boolean).join(' ');
      }
      return acc;
    },
    '',
  );
  const isEscrowedOnPropertyVisible = rowToEdit.escrowedOnProperty > 0;
  const escrowedOnPropertyText =
    ESCROWED_ON_PROPERTY_OPTIONS.find(
      (option) => option.value === rowToEdit.escrowedOnProperty,
    )?.text ?? '';

  const propertyStatusOptions =
    SUBJECT_PROPERTY_PROPERTY_STATUS_OPTIONS[String(isSubjectProperty)] ?? [];

  const getOccupancyTypeOptions = (
    propertyType: number,
    propertyStatus: number,
  ): FormOption[] => {
    const isTwoToFour =
      propertyType === REO_PROPERTY_TYPE.TWO_TO_4_UNIT_DWELLING;
    if (!isSubjectProperty && !isTwoToFour) {
      return NON_SUBJECT_PROP_NOT_TWO_FOUR_UNIT_OCCUPANCY[propertyStatus] ?? [];
    }
    if (!isSubjectProperty && isTwoToFour) {
      return NON_SUBJECT_PROP_TWO_FOUR_UNIT_OCCUPANCY[propertyStatus] ?? [];
    }
    if (isSubjectProperty && !isTwoToFour) {
      return SUBJECT_PROP_NOT_TWO_FOUR_UNIT_OCCUPANCY[propertyStatus] ?? [];
    }
    return SUBJECT_PROP_TWO_FOUR_UNIT_OCCUPANCY[propertyStatus] ?? [];
  };

  const [currentOccupancyOptions, setCurrentOccupancyOptions] = useState<
    FormOption[]
  >(() =>
    getOccupancyTypeOptions(
      Number(rowToEdit.propertyTypeId),
      Number(rowToEdit.propertyStatus),
    ),
  );

  const [
    isMonthlyAssociationDuesEditable,
    setIsMonthlyAssociationDuesEditable,
  ] = useState(
    Object.values(REO_GROSS_RENTAL_INCOME_PROPERTY_TYPE).includes(
      Number(rowToEdit.propertyTypeId),
    ),
  );

  const [isGrossRentalIncomeEnabled, setIsGrossRentalIncomeEnabled] = useState(
    rowToEdit.propertyStatus === REO_PROPERTY_STATUS.RENTAL,
  );

  const prevPropertyTypeRef = useRef<number>(Number(rowToEdit.propertyTypeId));

  const { form, handleSubmit } = useQuestionBuilder({
    formData: getDefaultValues(rowToEdit) as RealEstateOwnedSummaryFormData,
    screenData: {
      isWriteAccessDisabled,
    },
    listeners: {
      onChange: ({ fieldApi, formApi }) => {
        const name = fieldApi.name as string;
        const value = fieldApi.state.value;

        if (name === 'propertyType') {
          const numValue = Number(value);
          const prevPropertyType = prevPropertyTypeRef.current;
          prevPropertyTypeRef.current = numValue;
          setIsMonthlyAssociationDuesEditable(
            Object.values(REO_GROSS_RENTAL_INCOME_PROPERTY_TYPE).includes(
              numValue,
            ),
          );
          if (
            numValue === REO_PROPERTY_TYPE.TWO_TO_4_UNIT_DWELLING ||
            prevPropertyType === REO_PROPERTY_TYPE.TWO_TO_4_UNIT_DWELLING
          ) {
            formApi.setFieldValue('propertyStatus', '');
            formApi.setFieldValue('occupancyType', '');
            setCurrentOccupancyOptions([]);
          }
        }

        if (name === 'propertyStatus') {
          const numValue = Number(value);
          if (!numValue) return;
          setIsGrossRentalIncomeEnabled(
            numValue === REO_PROPERTY_STATUS.RENTAL,
          );
          formApi.setFieldValue('occupancyType', '');
          setCurrentOccupancyOptions(
            getOccupancyTypeOptions(
              Number(formApi.state.values.propertyType),
              numValue,
            ),
          );
          if (
            numValue === REO_PROPERTY_STATUS.RENTAL &&
            rowToEdit.grossRentalIncome !== ''
          ) {
            formApi.setFieldValue(
              'grossRentalIncome',
              Number(rowToEdit.grossRentalIncome),
            );
            formApi.setFieldValue('hasGrossRentalIncomeChanged', false);
          } else {
            formApi.setFieldValue('grossRentalIncome', 0);
            formApi.setFieldValue('hasGrossRentalIncomeChanged', true);
          }
        }

        if (name === 'country' && value !== 'US') {
          formApi.setFieldValue('state', '');
          formApi.setFieldValue('zipCode', '');
        }

        if (name === 'grossRentalIncome') {
          formApi.setFieldValue(
            'hasGrossRentalIncomeChanged',
            rowToEdit.grossRentalIncome !== value,
          );
        }
      },
    },
    hooks: {
      onSubmit: async (data) => {
        const parseNumber = (val: number | string | undefined | null) =>
          Number(val) || 0;

        const realEstateOwnedSummary: DtoV1RealEstateOwnedSaveRealEstateOwnedSummaryRequest =
          {
            obfuscatedLoanIdentifier,
            customerRecordIds: data.customerIdentifiers.map(
              (customerIdentifier) => ({
                obfuscatedLenderDatabaseId:
                  obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
                customerRecordId: Number(customerIdentifier),
              }),
            ),
            addressIdentifier:
              typeof rowToEdit.addressId === 'number'
                ? { addressId: rowToEdit.addressId, addressLdId: 0 }
                : rowToEdit.addressId,
            propertyAddress: {
              addressLine1: data.addressLine1,
              unit: data.unit,
              city: data.city,
              state: data.state,
              zipCode: data.zipCode,
              country: data.country,
            },
            propertyTypeId: data.propertyType as DtoV1EnumPropertyType,
            occupancyTypeId: data.occupancyType as DtoV1EnumOccupancyType,
            propertyStatusId: data.propertyStatus as DtoV1EnumPropertyStatus,
            presentvalue: parseNumber(data.marketValue),
            monthlyFloodInsurance: parseNumber(data.monthlyFloodInsurance),
            monthlyAssociationDues: parseNumber(data.monthlyAssociationDues),
            grossRentalIncome: parseNumber(data.grossRentalIncome),
            hasGrossRentalIncomeChanged: data.hasGrossRentalIncomeChanged,
            monthlyGroundRent: parseNumber(data.leaseGroundRents),
            isSubjectProperty: data.subjectProperty,
          };

        mutate(realEstateOwnedSummary);
      },
    },
  });

  return (
    <form.Provider>
      <Loan1003FormContainer
        onCloseClicked={closeForm}
        onSaveClicked={handleSubmit}
        toggleErrors={() => {}}
        isLoading={isLoading}
        readOnlyMode={isWriteAccessDisabled}
      >
        <Loan1003FormSection title="REO Information">
          <form.CheckboxGroup
            name="customerIdentifiers"
            label="Associated Borrower(s)"
            rules={{
              required:
                'There must be at least one name associated with the liability.',
            }}
            conditions={{
              isDisabled: {
                or: [isSubjectProperty, READONLY_CONDITION],
              },
            }}
            style={{
              paddingBottom: 0,
              paddingTop: '0.5rem',
            }}
            options={(allBorrowerOptions ?? []).map((borrower) => ({
              label: formatName(
                borrower.firstName,
                borrower.lastName,
                borrower.middleName,
                borrower.suffixName,
              ),
              value: borrower.customerRecordId,
            }))}
            hasBottomBorder={false}
          />
          <form.Grid
            name="reo information grid"
            columns={4}
            style={{
              paddingTop: 0,
            }}
          >
            <form.GridItem colSpan={2}>
              <form.Text
                name="addressLine1"
                label="Address Line 1"
                placeholder="Address Line 1"
                rules={{
                  required: true,
                }}
                conditions={{
                  isDisabled: READONLY_CONDITION,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Text
                name="unit"
                label="Unit #"
                placeholder="Unit Number"
                conditions={{
                  isDisabled: READONLY_CONDITION,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <div />
            </form.GridItem>
            <form.GridItem>
              <form.Text
                name="city"
                label="City"
                placeholder="City"
                conditions={{
                  isDisabled: READONLY_CONDITION,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Select
                name="state"
                label="State"
                placeholder="Select Option"
                options={US_STATES_LABEL_VALUE_ARRAY}
                conditions={{
                  isDisabled: or(
                    formIsNot('country', 'US'),
                    READONLY_CONDITION,
                  ),
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Select
                name="country"
                label="Country"
                placeholder="Select Option"
                options={COUNTRY_LABEL_VALUE_ARRAY}
                conditions={{
                  isDisabled: READONLY_CONDITION,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Text
                name="zipCode"
                label="Zip Code"
                placeholder="Zip Code"
                rules={{
                  onSubmit: {
                    type: 'string',
                    format: 'general',
                    pattern: String.raw`^(\d{5}(-\d{4})?)?$`,
                    message:
                      'Enter a valid zip code (e.g., 48234 or 48234-9589)',
                  },
                }}
                conditions={{
                  isDisabled: or(
                    formIsNot('country', 'US'),
                    READONLY_CONDITION,
                  ),
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Select
                name="propertyType"
                label="Property Type"
                placeholder="Select Option"
                rules={{
                  required: true,
                }}
                conditions={{
                  isDisabled: READONLY_CONDITION,
                }}
                options={dropdownOptions?.availablePropertyTypes ?? []}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Select
                name="propertyStatus"
                label="Property Status"
                placeholder="Select Option"
                rules={{
                  required: true,
                }}
                options={propertyStatusOptions}
                conditions={{
                  isDisabled: READONLY_CONDITION,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Select
                name="occupancyType"
                label="Intended Occupancy"
                placeholder="Select Option"
                rules={{
                  required: true,
                }}
                options={currentOccupancyOptions}
                conditions={{
                  isDisabled: READONLY_CONDITION,
                }}
              />
            </form.GridItem>
          </form.Grid>
        </Loan1003FormSection>
        <Loan1003FormSection title="Property Information">
          <form.Grid
            name="property information grid"
            columns={4}
            style={{
              padding: '0 0 0 1rem',
            }}
          >
            <form.GridItem colSpan={4}>
              <form.TextComponent
                name="associatedLiabilitiesText"
                text={
                  creditorNames.length > 0
                    ? `Associated Liabilities - ${creditorNames}`
                    : ''
                }
                variant="subtitleS"
                conditions={{
                  isVisible: isAssociatedLiabilitiesVisible,
                  isDisabled: READONLY_CONDITION,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Currency
                name="mortgagePayment"
                label="Mortgage Payment"
                conditions={{
                  isDisabled: true,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Currency
                name="mortgageBalance"
                label="Mortgage Balance"
                conditions={{
                  isDisabled: true,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Currency
                name="marketValue"
                label="Market Value"
                conditions={{
                  isDisabled: READONLY_CONDITION,
                }}
              />
            </form.GridItem>
            <form.GridItem colSpan={4}>
              <form.TextComponent
                name="escrowedOnPropertyText"
                text={escrowedOnPropertyText}
                conditions={{
                  isVisible: isEscrowedOnPropertyVisible,
                  isDisabled: READONLY_CONDITION,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Currency
                name="monthlyPropertyTaxes"
                label="Monthly Property Taxes"
                conditions={{
                  isDisabled: true,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Currency
                name="monthlyHazardInsurance"
                label="Monthly Hazard Insurance"
                conditions={{
                  isDisabled: true,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Currency
                name="monthlyFloodInsurance"
                label="Monthly Flood Insurance"
                conditions={{
                  isDisabled: READONLY_CONDITION,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Currency
                name="monthlyAssociationDues"
                label="Monthly Association Dues"
                conditions={{
                  isDisabled:
                    !isMonthlyAssociationDuesEditable || isWriteAccessDisabled,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Currency
                name="grossRentalIncome"
                label="Gross Rental Income"
                conditions={{
                  isDisabled:
                    !isGrossRentalIncomeEnabled || isWriteAccessDisabled,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Currency
                name="leaseGroundRents"
                label="Lease Ground Rents"
                conditions={{
                  isDisabled: isWriteAccessDisabled,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Currency
                name="totalMonthlyExpenses"
                label="Total Monthly Expenses"
                conditions={{
                  isDisabled: true,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Currency
                name="netRentalIncome"
                label="Net Rental Income"
                conditions={{
                  isDisabled: true,
                }}
              />
            </form.GridItem>
          </form.Grid>
        </Loan1003FormSection>
      </Loan1003FormContainer>
    </form.Provider>
  );
}
